/**
 * React Hook for Central Discovery Queue
 * Provides React components with access to the central discovery queue
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import centralDiscoveryQueue from '../utils/centralDiscoveryQueue';

/**
 * Custom hook for using the central discovery queue in React components
 * @param {string} sessionId - Current session ID
 * @param {Array} initialRequirements - Initial ICP requirements (optional)
 * @param {string} userId - User ID (optional)
 * @returns {Object} Queue state and methods
 */
export function useCentralDiscoveryQueue(sessionId, initialRequirements = []) {
  const [queueItems, setQueueItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    progress_percentage: 0,
    by_topic: {},
    last_saved: null
  });

  const listenerRef = useRef(null);
  const initializedRef = useRef(false);

  // Queue change listener
  const handleQueueChange = useCallback((items) => {
    setQueueItems(items);
    setStats(centralDiscoveryQueue.getStats());
  }, []);

  // Initialize queue when sessionId changes
  useEffect(() => {
    if (!sessionId) {
      setQueueItems([]);
      setStats({
        total: 0,
        completed: 0,
        pending: 0,
        progress_percentage: 0,
        by_topic: {},
        last_saved: null
      });
      setIsLoading(false);
      initializedRef.current = false;
      return;
    }

    if (initializedRef.current && centralDiscoveryQueue.sessionId === sessionId) {
      // Already initialized for this session
      setQueueItems(centralDiscoveryQueue.getAllItems());
      setStats(centralDiscoveryQueue.getStats());
      setIsLoading(false);
      return;
    }

    const initializeQueue = async () => {
      setIsLoading(true);
      setError(null);

      console.log('🔄 CENTRAL QUEUE HOOK: Initializing queue for session:', sessionId, 'with initialRequirements:', initialRequirements);

      try {
        await centralDiscoveryQueue.initialize(sessionId, initialRequirements);
        const items = centralDiscoveryQueue.getAllItems();
        const stats = centralDiscoveryQueue.getStats();
        
        console.log('🔄 CENTRAL QUEUE HOOK: Initialization complete:', {
          itemsCount: items.length,
          stats,
          items: items.map(item => ({
            section_id: item.section_id,
            topic: item.topic,
            disco_done: item.disco_done
          }))
        });
        
        setQueueItems(items);
        setStats(stats);
        initializedRef.current = true;
      } catch (err) {
        console.error('❌ CENTRAL QUEUE HOOK: Error initializing central discovery queue:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    initializeQueue();
  }, [sessionId]); // Remove initialRequirements from dependencies to prevent infinite loop

  // Set up listener for queue changes
  useEffect(() => {
    if (listenerRef.current) {
      centralDiscoveryQueue.removeListener(listenerRef.current);
    }

    listenerRef.current = handleQueueChange;
    centralDiscoveryQueue.addListener(listenerRef.current);

    // Initial update
    setQueueItems(centralDiscoveryQueue.getAllItems());
    setStats(centralDiscoveryQueue.getStats());

    return () => {
      if (listenerRef.current) {
        centralDiscoveryQueue.removeListener(listenerRef.current);
      }
    };
  }, [handleQueueChange]);

  // Add or update item in queue
  const addOrUpdateItem = useCallback(async (item, merge = true) => {
    try {
      await centralDiscoveryQueue.addOrUpdateItem(item, merge);
    } catch (err) {
      console.error('Error adding/updating queue item:', err);
      setError(err.message);
    }
  }, []);

  // Mark item as discovered
  const markAsDiscovered = useCallback(async (sectionId, boolStr, componentData = null) => {
    try {
      await centralDiscoveryQueue.markAsDiscovered(sectionId, boolStr, componentData);
    } catch (err) {
      console.error('Error marking item as discovered:', err);
      setError(err.message);
    }
  }, []);

  // Get items by topic
  const getItemsByTopic = useCallback((topic) => {
    return centralDiscoveryQueue.getItemsByTopic(topic);
  }, []);

  // Get pending items
  const getPendingItems = useCallback(() => {
    return centralDiscoveryQueue.getPendingItems();
  }, []);

  // Get completed items
  const getCompletedItems = useCallback(() => {
    return centralDiscoveryQueue.getCompletedItems();
  }, []);

  // Get next pending item for topic
  const getNextPendingForTopic = useCallback((topic) => {
    return centralDiscoveryQueue.getNextPendingForTopic(topic);
  }, []);

  // Check if topic is fully discovered
  const isTopicFullyDiscovered = useCallback((topic) => {
    return centralDiscoveryQueue.isTopicFullyDiscovered(topic);
  }, []);

  // Get current ICP requirements
  const getCurrentICPRequirements = useCallback(() => {
    return centralDiscoveryQueue.getCurrentICPRequirements();
  }, []);

  // Force save to database
  const forceSave = useCallback(async () => {
    try {
      await centralDiscoveryQueue.saveToDatabase();
    } catch (err) {
      console.error('Error force saving queue:', err);
      setError(err.message);
    }
  }, []);

  // Update section type
  const updateSectionType = useCallback(async (sectionId, sectionType) => {
    try {
      await centralDiscoveryQueue.updateSectionType(sectionId, sectionType);
    } catch (err) {
      console.error('Error updating section type:', err);
      setError(err.message);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    queueItems,
    isLoading,
    error,
    stats,

    // Methods
    addOrUpdateItem,
    markAsDiscovered,
    getItemsByTopic,
    getPendingItems,
    getCompletedItems,
    getNextPendingForTopic,
    isTopicFullyDiscovered,
    getCurrentICPRequirements,
    updateSectionType,
    forceSave,
    clearError,

    // Computed values
    totalItems: stats.total,
    completedItems: stats.completed,
    pendingItems: stats.pending,
    progressPercentage: stats.progress_percentage,
    topicStats: stats.by_topic,
    lastSaved: stats.last_saved
  };
}

export default useCentralDiscoveryQueue;
